import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Clock,
  HardDrive,
  Camera,
  Settings as SettingsIcon,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from "lucide-react";

import PageWrapper from "../../components/layout/PageWrapper";
import Button from "../../components/forms/Button";
import Toast from "../../components/ui/Toast";
import useToast from "../../hooks/useToast";
import { monitoringService } from "../../api/monitoringService";
import { axiosPrivate } from "../../api/axios";

import MonitoringFilters from "./components/MonitoringFilters";
import LiveSessionsPanel from "./components/LiveSessionsPanel";
import SessionsTable from "./components/SessionsTable";
import {
  daysAgoStr,
  employeeName,
  formatBytes,
  formatWorked,
  todayStr,
} from "./utils/format";

const LIVE_POLL_MS = 15000;

const StatCard = ({ icon: Icon, label, value, tone = "accent" }) => {
  const tones = {
    accent: "bg-primary-accent/15 text-primary-accent",
    green: "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400",
    blue: "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400",
    gray: "bg-gray-100 dark:bg-dark-200 text-gray-600 dark:text-light-400",
  };
  return (
    <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-4">
      <div className="flex items-center gap-3">
        <div
          className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones[tone]}`}
        >
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-gray-500 dark:text-light-500">{label}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-light-100 truncate">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
};

const AdminMonitoring = () => {
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();

  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [active, setActive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLoading, setActiveLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  // `draft` is what the inputs hold; `filters` is what has been applied. Keeping
  // them separate is what makes the Apply button meaningful.
  const initialFilters = {
    employeeId: "",
    startDate: daysAgoStr(6),
    endDate: todayStr(),
    status: "all",
  };
  const [draft, setDraft] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);

  const fetchSessions = useCallback(
    async (nextPage = 1, applied = filters) => {
      setLoading(true);
      try {
        const data = await monitoringService.getSessions({
          ...applied,
          page: nextPage,
          limit: 25,
        });
        setRecords(data.records || []);
        setPage(data.page || 1);
        setTotalPages(data.totalPages || 1);
        setTotal(data.total || 0);
      } catch (err) {
        showToast(
          err?.response?.data?.message || "Could not load work sessions",
          "error",
        );
      } finally {
        setLoading(false);
      }
    },
    [filters, showToast],
  );

  const fetchActive = useCallback(async () => {
    try {
      const data = await monitoringService.getActiveSessions();
      setActive(data.records || []);
    } catch {
      // Non-fatal: the live strip is a nicety, the table below is the record.
    } finally {
      setActiveLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions(1, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    fetchActive();
    const id = setInterval(fetchActive, LIVE_POLL_MS);
    return () => clearInterval(id);
  }, [fetchActive]);

  useEffect(() => {
    axiosPrivate
      .get("/api/employees")
      .then((res) => {
        const list = Array.isArray(res.data)
          ? res.data
          : res.data?.employees || res.data?.records || [];
        setEmployees(list.filter((e) => !e.isSystemAccount));
      })
      .catch(() => setEmployees([]));
  }, []);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return records;
    return records.filter((r) =>
      employeeName(r.employeeId).toLowerCase().includes(term),
    );
  }, [records, searchTerm]);

  const stats = useMemo(() => {
    const today = todayStr();
    const todays = records.filter((r) => r.date === today);
    return {
      activeNow: active.length,
      sessionsToday: todays.length,
      workedToday: todays.reduce((sum, r) => sum + (r.workedSeconds || 0), 0),
      bytesInRange: records.reduce(
        (sum, r) => sum + (r.recordedBytes || 0) + (r.screenshotBytes || 0),
        0,
      ),
      shotsInRange: records.reduce(
        (sum, r) => sum + (r.screenshotCount || 0),
        0,
      ),
    };
  }, [records, active]);

  return (
    <PageWrapper
      title="Monitoring"
      subtitle="Work sessions, screenshots and screen recordings"
      actions={
        <>
          <button
            onClick={() => {
              fetchSessions(page);
              fetchActive();
            }}
            disabled={loading}
            className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 dark:text-light-400 border border-gray-200 dark:border-dark-200 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-300 disabled:opacity-40 transition-colors bg-white dark:bg-dark-100"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <Button
            size="sm"
            onClick={() => navigate("/management/monitoring/settings")}
            className="bg-white dark:bg-dark-100 border border-gray-200 dark:border-dark-200 text-gray-700 dark:text-light-200 hover:bg-gray-100 dark:hover:bg-dark-300 rounded-lg"
          >
            <SettingsIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Settings</span>
          </Button>
        </>
      }
    >
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatCard
          icon={Users}
          label="Working now"
          value={stats.activeNow}
          tone="green"
        />
        <StatCard
          icon={Clock}
          label="Worked today"
          value={formatWorked(stats.workedToday)}
          tone="accent"
        />
        <StatCard
          icon={Camera}
          label="Screenshots in range"
          value={stats.shotsInRange}
          tone="blue"
        />
        <StatCard
          icon={HardDrive}
          label="Storage in range"
          value={formatBytes(stats.bytesInRange)}
          tone="gray"
        />
      </div>

      <LiveSessionsPanel records={active} loading={activeLoading} />

      <MonitoringFilters
        employees={employees}
        draft={draft}
        onDraftChange={setDraft}
        onApply={() => setFilters(draft)}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        loading={loading}
      />

      {/* Sessions */}
      <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-accent" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500 dark:text-light-500">
            {records.length === 0
              ? "No work sessions in this range"
              : "No sessions match that name"}
          </div>
        ) : (
          <>
            <SessionsTable records={filtered} />

            <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-100 flex items-center justify-between">
              <p className="text-sm text-gray-500 dark:text-light-500">
                {total} session{total === 1 ? "" : "s"} · page {page} of{" "}
                {totalPages}
              </p>
              {totalPages > 1 && (
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchSessions(page - 1)}
                    disabled={page <= 1}
                    className="p-2 rounded-lg border border-gray-200 dark:border-dark-100 text-gray-600 dark:text-light-400 hover:bg-gray-100 dark:hover:bg-dark-300 disabled:opacity-40 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => fetchSessions(page + 1)}
                    disabled={page >= totalPages}
                    className="p-2 rounded-lg border border-gray-200 dark:border-dark-100 text-gray-600 dark:text-light-400 hover:bg-gray-100 dark:hover:bg-dark-300 disabled:opacity-40 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <Toast {...toast} onClose={hideToast} />
    </PageWrapper>
  );
};

export default AdminMonitoring;
